import { useMemo } from 'react';
import { t } from '@lingui/macro';

import { useId } from '../../page-framework/panel/Panel';
import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import PanelActions from '../../page-framework/panel/PanelActions';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import ChartTypeForm from '../../charts/ipm/ChartTypeForm';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { timeRangeFilterId, virtualMachineFilterId } from '../../filters';
import useChartDisplayType from '../../../utilities/UseChartDisplayType';

const title = t`Virtual Machine Memory Utilization`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'server',
        metrics: ['statusMemUtil', 'avgMem', 'vmConsumedB', 'vmActiveB', 'vmGrantedB'],
    },
};

const PERCENT = 'PERCENT';
const BYTES = 'BYTES';

const typeConfig = {
    [PERCENT]: {
        yAxis: [
            {
                title: {
                    text: t`Utilization (%)`,
                },
                ceiling: 100,
            },
        ],
        tooltip: BuiltInTooltip,
        showGranularity: true,
        statusMetric: 'statusMemUtil',
        chartTrends: [
            {
                formatter: FORMATTER_TYPES.PERCENT,
                units: '%',
                chartType: 'spline',
                key: 'avgMem',
                label: t`Usage`,
                multiplier: 1,
                forceDecimals: true,
            },
        ],
    },
    [BYTES]: {
        yAxis: [
            {
                title: {
                    text: t`Utilization (Bytes)`,
                },
                ceiling: undefined,
            },
        ],
        tooltip: BuiltInTooltip,
        showGranularity: true,
        statusMetric: 'statusMemUtil',
        chartTrends: [
            {
                formatter: FORMATTER_TYPES.BYTE,
                chartType: 'spline',
                key: 'vmActiveB',
                label: t`Usage`,
                multiplier: 1,
                forceDecimals: true,
            },
            {
                formatter: FORMATTER_TYPES.BYTE,
                chartType: 'spline',
                key: 'vmConsumedB',
                label: t`Consumed`,
                multiplier: 1,
                forceDecimals: true,
            },
            {
                formatter: FORMATTER_TYPES.BYTE,
                chartType: 'spline',
                key: 'vmGrantedB',
                label: t`Granted`,
                multiplier: 1,
                forceDecimals: true,
            },
        ],
    },
};

function useChartConfig(type) {
    const colorUsage = useMetricColor('serverCpu');
    const colorConsumed = useMetricColor('serverMemActive');
    const colorGranted = useMetricColor('serverMemGranted');

    return useMemo(() => {
        const config = typeConfig[type];
        config.chartTrends[0].color = colorUsage;
        if (type === BYTES) {
            config.chartTrends[1].color = colorConsumed;
            config.chartTrends[2].color = colorGranted;
        }
        return config;
    }, [type, colorUsage, colorConsumed, colorGranted]);
}

const selections = [
    { value: PERCENT, label: '%' },
    { value: BYTES, label: t`Bytes` },
];

function VirtualMachineMemoryUtilizationPanel() {
    const id = useId();
    const pageStateKey = `${id}-ChartType`;
    const [chartDisplayType, setChartDisplayType] = useChartDisplayType(pageStateKey, PERCENT);
    const data = usePanelDataService(dataSource);
    const chartConfig = useChartConfig(chartDisplayType);
    const chartData = data?.data?.[0];

    if (!data) return null;

    return (
        <>
            <PanelActions>
                <ChartTypeForm value={chartDisplayType} onChangeType={setChartDisplayType} selections={selections} />
            </PanelActions>
            <PerformanceChart chartDef={chartConfig} data={chartData} />
        </>
    );
}

VirtualMachineMemoryUtilizationPanel.propTypes = panelPropTypes;

VirtualMachineMemoryUtilizationPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'VmwareVmMemoryUtilTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.VIRTUAL_MACHINE,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Memory utilization trend for a selected virtual machine (VM)`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, virtualMachineFilterId],
        optional: [],
    },
})(VirtualMachineMemoryUtilizationPanel);
